import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StaleTime } from "newui/store/tanstack-base";
import {
	getRfxNotificationManagement,
	putRfxNotificationRules,
} from "../api/rfx-notification-mgmt-api";
import { RFX_NOTIFICATION_MGMT_QUERY_KEYS } from "../constants/query-keys";
import type {
	CoverageViolationResponse,
	RfxNotificationRulesWriteRequest,
} from "../types/rfx-notification-mgmt.types";

export const useRfxNotificationMgmt = (rfxId: number) => {
	return useQuery({
		queryKey: RFX_NOTIFICATION_MGMT_QUERY_KEYS.management(rfxId),
		queryFn: () => getRfxNotificationManagement(rfxId),
		enabled: Number.isFinite(rfxId) && rfxId > 0,
		staleTime: StaleTime.Short,
	});
};

// Narrows an unknown mutation error to the C17 coverage-violation envelope
// (shared shape across 422 and 409 — status is what distinguishes them).
export const asCoverageViolationResponse = (
	error: unknown
): { status: number; body: CoverageViolationResponse } | null => {
	const response = (
		error as { response?: { status?: number; data?: CoverageViolationResponse } }
	)?.response;

	if (
		(response?.status === 422 || response?.status === 409) &&
		response.data &&
		Array.isArray(response.data.violations)
	) {
		return { status: response.status, body: response.data };
	}

	return null;
};

export const useRfxNotificationRulesMutation = (rfxId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: RfxNotificationRulesWriteRequest) =>
			putRfxNotificationRules(rfxId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: RFX_NOTIFICATION_MGMT_QUERY_KEYS.management(rfxId),
			});
		},
	});
};
